import React, { useState, useEffect } from 'react';
import { Modal, Button, Checkbox, Card, Badge, Divider, Typography, Space, DatePicker, TimePicker, Input, message } from 'antd';
import { ClockCircleOutlined, UserOutlined, ExclamationCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { groupTasksForDisplay } from '../utils/crisisTaskEngine';
import '../styles/CrisisTaskPreview.css';

const { Title, Text, Paragraph } = Typography;

const CrisisTaskPreviewModal = ({ 
  visible, 
  onClose, 
  onConfirm, 
  generatedTasks = [], 
  crisisType, 
  loading = false 
}) => {
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [taskModifications, setTaskModifications] = useState({});
  const [groupedTasks, setGroupedTasks] = useState({});

  useEffect(() => {
    if (generatedTasks.length > 0) {
      // Group tasks for display
      const grouped = groupTasksForDisplay(generatedTasks);
      setGroupedTasks(grouped);
      
      // Auto-select all critical tasks by default
      const criticalTaskIds = new Set();
      Object.values(grouped.critical).flat().forEach(task => {
        criticalTaskIds.add(task.id);
      });
      setSelectedTasks(criticalTaskIds);
    }
  }, [generatedTasks]);

  const handleTaskToggle = (taskId, checked) => {
    const newSelected = new Set(selectedTasks);
    if (checked) {
      newSelected.add(taskId);
    } else {
      newSelected.delete(taskId);
    }
    setSelectedTasks(newSelected);
  };

  const handleTaskModification = (taskId, field, value) => {
    setTaskModifications(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [field]: value
      }
    }));
  };

  const handleBulkSelect = (priority, checked) => {
    const newSelected = new Set(selectedTasks);
    const tasksInPriority = Object.values(groupedTasks[priority] || {}).flat();
    
    tasksInPriority.forEach(task => {
      if (checked) {
        newSelected.add(task.id);
      } else {
        newSelected.delete(task.id);
      }
    });
    
    setSelectedTasks(newSelected);
  };

  const handleConfirm = () => {
    const selectedTasksArray = generatedTasks.filter(task => selectedTasks.has(task.id));
    
    // Apply modifications to selected tasks
    const modifiedTasks = selectedTasksArray.map(task => {
      const modifications = taskModifications[task.id] || {};
      return {
        ...task,
        ...modifications,
        // Ensure we have proper date objects
        suggestedDueDate: modifications.dueDate ? 
          (moment.isMoment(modifications.dueDate) ? modifications.dueDate.toDate() : modifications.dueDate) :
          task.suggestedDueDate
      };
    });
    
    onConfirm(modifiedTasks);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return '#ff4d4f';
      case 'recommended': return '#faad14';
      case 'optional': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical': return <ExclamationCircleOutlined />;
      case 'recommended': return <ClockCircleOutlined />;
      case 'optional': return <CheckCircleOutlined />;
      default: return null;
    }
  };

  const formatDueDate = (task) => {
    const modifications = taskModifications[task.id];
    if (modifications?.noDueDate) return 'No due date';
    
    const dueDate = modifications?.dueDate || task.suggestedDueDate;
    if (!dueDate) return 'No due date';
    
    return moment(dueDate).format('DD/MM/YYYY HH:mm');
  };

  const renderTaskCard = (task) => {
    const isSelected = selectedTasks.has(task.id);
    const modifications = taskModifications[task.id] || {};
    
    return (
      <Card 
        key={task.id}
        size="small" 
        className={`task-preview-card ${isSelected ? 'selected' : ''}`}
        style={{ 
          marginBottom: 8,
          border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
          backgroundColor: isSelected ? '#f6ffed' : '#ffffff'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <Checkbox
            checked={isSelected}
            onChange={(e) => handleTaskToggle(task.id, e.target.checked)}
          />
          
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <Text strong>{task.name}</Text>
              <Badge 
                color={getPriorityColor(task.priority)} 
                text={task.priority?.toUpperCase()} 
              />
            </div>
            
            <Paragraph style={{ margin: 0, fontSize: '13px', color: '#666' }}>
              {task.description}
            </Paragraph>
            
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 16, fontSize: '12px' }}>
              <Space size={4}>
                <UserOutlined />
                <Text type="secondary">{task.roleDisplayName}</Text>
              </Space>
              
              <Space size={4}>
                <ClockCircleOutlined />
                <Text type="secondary">{formatDueDate(task)}</Text>
              </Space>
              
              {task.estimatedDurationMinutes && (
                <Text type="secondary">
                  ~{task.estimatedDurationMinutes}min
                </Text>
              )}
            </div>
            
            {/* Task modification controls */}
            {isSelected && (
              <div style={{ marginTop: 12, padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Checkbox
                    checked={modifications.noDueDate || task.noDueDate}
                    onChange={(e) => handleTaskModification(task.id, 'noDueDate', e.target.checked)}
                  >
                    No due date
                  </Checkbox>
                  
                  {!modifications.noDueDate && !task.noDueDate && (
                    <>
                      <DatePicker
                        size="small"
                        value={modifications.dueDate ? moment(modifications.dueDate) : (task.suggestedDueDate ? moment(task.suggestedDueDate) : null)}
                        onChange={(date) => handleTaskModification(task.id, 'dueDate', date)}
                        format="DD/MM/YYYY"
                        placeholder="Due date"
                        style={{ width: 120 }}
                      />
                      
                      <TimePicker
                        size="small"
                        value={modifications.dueDate ? moment(modifications.dueDate) : (task.suggestedDueDate ? moment(task.suggestedDueDate) : null)}
                        onChange={(time) => {
                          const currentDate = modifications.dueDate ? moment(modifications.dueDate) : (task.suggestedDueDate ? moment(task.suggestedDueDate) : moment());
                          if (time) {
                            const newDateTime = currentDate.clone()
                              .hour(time.hour())
                              .minute(time.minute());
                            handleTaskModification(task.id, 'dueDate', newDateTime);
                          }
                        }}
                        format="HH:mm"
                        placeholder="Time"
                        style={{ width: 80 }}
                      />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  };

  const renderPrioritySection = (priority, priorityData) => {
    const allTasks = Object.values(priorityData).flat();
    if (allTasks.length === 0) return null;
    
    const selectedInPriority = allTasks.filter(task => selectedTasks.has(task.id)).length;
    const allSelected = selectedInPriority === allTasks.length;
    const someSelected = selectedInPriority > 0 && selectedInPriority < allTasks.length;
    
    return (
      <div key={priority} style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={(e) => handleBulkSelect(priority, e.target.checked)}
          />
          <Title level={5} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            {getPriorityIcon(priority)}
            {priority.charAt(0).toUpperCase() + priority.slice(1)} Tasks
            <Badge count={allTasks.length} style={{ backgroundColor: getPriorityColor(priority) }} />
          </Title>
        </div>
        
        {Object.entries(priorityData).map(([phase, tasks]) => {
          if (tasks.length === 0) return null;
          
          return (
            <div key={phase} style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', fontWeight: 'bold' }}>
                {tasks[0]?.phaseDisplayName || phase}
              </Text>
              <div style={{ marginTop: 8 }}>
                {tasks.map(renderTaskCard)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const totalTasks = generatedTasks.length;
  const selectedCount = selectedTasks.size;

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Crisis Response Tasks
          </Title>
          <Text type="secondary">
            Suggested tasks for {crisisType} incident
          </Text>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      className="crisis-task-modal"
      footer={[
        <Button key="cancel" onClick={onClose}>
          Skip Tasks
        </Button>,
        <Button 
          key="confirm" 
          type="primary" 
          onClick={handleConfirm}
          loading={loading}
          disabled={selectedCount === 0}
        >
          Add {selectedCount} Task{selectedCount !== 1 ? 's' : ''} to Timeline
        </Button>
      ]}
      style={{ top: 20 }}
              styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
    >
      {totalTasks === 0 ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Text type="secondary">
            No task templates available for this crisis type yet.
          </Text>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f0f9ff', borderRadius: 6, border: '1px solid #bae7ff' }}>
            <Text>
              We've identified <strong>{totalTasks} recommended tasks</strong> for your {crisisType} incident. 
              Review and select which tasks to add to your timeline. You can modify due dates and assignments before confirming.
            </Text>
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              Selected: {selectedCount} of {totalTasks} tasks
            </Text>
          </div>
          
          <Divider />
          
          {Object.entries(groupedTasks).map(([priority, priorityData]) => 
            renderPrioritySection(priority, priorityData)
          )}
        </>
      )}
    </Modal>
  );
};

export default CrisisTaskPreviewModal; 